import React from 'react';
import { Link } from 'react-router';
import { Badge } from '@rezi/ui/badge';
import { Card } from '@rezi/ui/card';
import { useDocCategories } from '../docsRegistry';
// import DocsSearch from '../components/DocsSearch';
import * as microservices from '../document-lists';

// const getStatusColor = (status: string) => {
//   switch (status.toLowerCase()) {
//     case 'active':
//       return 'green';
//     case 'draft':
//       return 'yellow';
//     case 'archived':
//       return 'gray';
//     default:
//       return 'blue';
//   }
// };

const DocsIndex = () => {
  const categories = useDocCategories();
  const publicCategories = categories.filter(
    (category) =>
      (category.rootDoc ?? category.docs[0])?.visibility?.toLowerCase() ===
      'public',
  );
  const internalCategories = categories.filter(
    (category) =>
      (category.rootDoc ?? category.docs[0])?.visibility?.toLowerCase() ===
      'internal',
  );
  const confidentialCategories = categories.filter(
    (category) =>
      (category.rootDoc ?? category.docs[0])?.visibility?.toLowerCase() ===
      'confidential',
  );

  const renderAPIDoc = (
    items: { [key: string]: string }[],
    emptyMessage: string,
  ) => (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {items.length === 0 && (
        <Card className="md:col-span-2 xl:col-span-3">
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        </Card>
      )}
      {items.map((apiDoc) => {
        if (!apiDoc.fileName) {
          return null;
        }
        return (
          <Link
            key={apiDoc.fileName}
            to={`/apis/${apiDoc.fileName.replaceAll('.html', '')}`}
            className="group"
          >
            <Card className="hover:bg-choice-list-item-hover h-full transition">
              <div className="flex items-center justify-between">
                <span
                  className={`text-xs font-semibold tracking-[0.12em] text-gray-400 uppercase`}
                >
                  internal ONLY
                </span>
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <h2 className="text-text group-hover:text-rezi-blue-600 text-lg font-semibold">
                  {apiDoc.serviceName ?? ''}
                </h2>
                {apiDoc.fileName.includes('rezi-api-docs') ? (
                  <p className="text-sm text-gray-500">Client-facing APIs</p>
                ) : (
                  ''
                )}
                <p className="text-sm text-gray-500"></p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {['swagger', 'api doc'].map((tag) => (
                    <Badge
                      key={tag}
                      label={tag}
                      color="light-gray"
                      size="small"
                    />
                  ))}
                </div>
              </div>
            </Card>
          </Link>
        );
      })}
    </div>
  );

  const renderCategories = (items: typeof categories, emptyMessage: string) => (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {items.length === 0 && (
        <Card className="md:col-span-2 xl:col-span-3">
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        </Card>
      )}
      {items.map((category) => {
        const doc = category.rootDoc ?? category.docs[0];
        if (!doc) {
          return null;
        }
        const visibility = doc.visibility.toLowerCase();

        return (
          <Link
            key={category.id}
            to={`/category/${category.id}`}
            className="group"
          >
            <Card className="hover:bg-choice-list-item-hover h-full transition">
              <div className="flex items-center justify-between">
                <span
                  className={`text-xs font-semibold tracking-[0.12em] uppercase ${
                    visibility === 'confidential'
                      ? 'text-red-500'
                      : 'text-gray-400'
                  }`}
                >
                  {visibility === 'internal' || visibility === 'confidential'
                    ? `${doc.visibility} ONLY`
                    : doc.visibility}
                </span>
              </div>
              <div className="mt-4 flex flex-col gap-2">
                <h2 className="text-text group-hover:text-rezi-blue-600 text-lg font-semibold">
                  {category.title}
                </h2>
                <p className="text-sm text-gray-500">
                  {category.docs.length} docs
                </p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {doc.tags.map((tag) => (
                    <Badge
                      key={tag}
                      label={tag}
                      color="light-gray"
                      size="small"
                    />
                  ))}
                </div>
              </div>
            </Card>
          </Link>
        );
      })}
    </div>
  );

  return (
    <section className="flex flex-col gap-8">
      <div>
        <h1 className="text-text text-2xl font-semibold">
          Documentation Catalogue
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          This catalog is generated from docs living next to the code.
        </p>
      </div>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <h2 className="text-text text-lg font-semibold">Public</h2>
          {renderCategories(publicCategories, 'No public docs yet.')}
        </div>
        <div className="flex flex-col gap-3">
          <h2 className="text-text text-lg font-semibold">Internal</h2>
          {renderCategories(internalCategories, 'No internal docs yet.')}
        </div>
        <div className="flex flex-col gap-3">
          <h2 className="text-text text-lg font-semibold">Confidential</h2>
          {renderCategories(
            confidentialCategories,
            'No confidential docs yet.',
          )}
        </div>
        <div className="flex flex-col gap-3">
          <h2 className="text-text text-lg font-semibold">
            API Documents for Microservices
          </h2>
          {renderAPIDoc(microservices.default, 'No API Docs yet')}
        </div>
      </div>
    </section>
  );
};

export default DocsIndex;
